// §19.12 — Accept-card UI. Renders a tree_proposal JSON payload (emitted
// by the Super Agent in a fenced ```tree_proposal block) as a structured
// card with Accept / Refine / Discard.
//
// Flow:
//   1. Super Agent decomposes user's intent.
//   2. Before calling provision_agent_tree, it emits a fenced
//      ```tree_proposal block in its reply containing the JSON args.
//   3. AgentBuilderChat detects the block, hands the parsed JSON to
//      this card.
//   4. User picks Accept (sends "Yes, create it.") / Refine (text input
//      → sends user's edit instruction back to LLM) / Discard (local
//      dismiss; conversation continues but card cannot be re-Accepted).
//
// Inline expert-edit dropdown + edit-mode diff overlay are deferred
// follow-ups (per §19.16 row 33 the card MVP needs Accept/Refine/Discard
// to be customer-handable).

import React, { useState, useMemo } from "react";
import { COLORS } from "../../theme/colors";
import ConfigureIntegrationModal from "./ConfigureIntegrationModal";
import { useAuth } from "../../contexts/AuthContext";

// §19 patch K-2 — author-mutating affordances on the Accept-card are
// hidden for non-developer roles. Backend already enforces via
// require_developer + the ctx.tenant_id flow on every Super Agent
// tool; this is defense-in-depth + UX (avoid surfacing buttons that
// would 403 server-side). Read-only viewing of the proposal is fine
// for any role — only mutation is gated.
const _DEV_ROLES = ["owner", "administrator", "developer"];


const ARCHETYPE_BADGE = {
  knowledge:   { label: "knowledge",   color: COLORS.cyan },
  openapi:     { label: "openapi",     color: COLORS.purple },
  mcp:         { label: "mcp",         color: COLORS.orange },
  web:         { label: "web",         color: COLORS.green },
  specialized: { label: "specialized", color: COLORS.textMuted },
};


// Exported for unit testing (repo pattern: expose pure logic from a component
// file so it's testable without a full render).
export function _archBadge(arch) {
  const key = (arch || "").toLowerCase();
  return ARCHETYPE_BADGE[key] || { label: arch || "expert", color: COLORS.textMuted };
}


// §19.12 / G.4 — compute the diff between an existing tree (fetched
// from /api/agents/<id>/tree on edit-mode chat open) and the proposed
// tree. Returns {byProposalIdx: {added|modified|unchanged}, removed:
// [{name, archetype}]} so the renderer can color rows accordingly.
//
// Match strategy: by expert name (case-insensitive). Stable enough for
// v1 — supervisor experts have unique human-readable names. When two
// experts share a name (rare), the first-match-wins; the second is
// flagged as "added" which conservatively over-reports but never
// under-reports a real change.
export function _computeTreeDiff(existingTree, proposalExperts) {
  if (!existingTree) return { byProposalIdx: {}, removed: [] };
  const existingExperts = (existingTree.experts || existingTree.experts_full || []).map((e) => ({
    name: e.name || "",
    archetype: e.archetype || (e.metadata_json && e.metadata_json.archetype) || "specialized",
    system_prompt: e.system_prompt || "",
  }));
  const seenExisting = new Set();
  const byProposalIdx = {};
  proposalExperts.forEach((p, idx) => {
    const pname = (p.name || "").toLowerCase().trim();
    if (!pname) { byProposalIdx[idx] = "added"; return; }
    const matchIdx = existingExperts.findIndex((e, ei) => !seenExisting.has(ei) && (e.name || "").toLowerCase().trim() === pname);
    if (matchIdx === -1) { byProposalIdx[idx] = "added"; return; }
    seenExisting.add(matchIdx);
    const e = existingExperts[matchIdx];
    const parche = (p.archetype || (p.metadata_json && p.metadata_json.archetype) || "specialized");
    const changed = (e.archetype !== parche) || ((p.system_prompt || "") !== (e.system_prompt || ""));
    byProposalIdx[idx] = changed ? "modified" : "unchanged";
  });
  const removed = existingExperts
    .map((e, ei) => seenExisting.has(ei) ? null : e)
    .filter(Boolean)
    .map((e) => ({ name: e.name, archetype: e.archetype }));
  return { byProposalIdx, removed };
}


function _diffStyle(state) {
  // Left-edge accent — visually similar to a `git diff` viewer.
  if (state === "added") return { borderLeft: "3px solid #10b981", paddingLeft: 6, background: "rgba(16,185,129,0.06)" };
  if (state === "removed") return { borderLeft: "3px solid #ef4444", paddingLeft: 6, background: "rgba(239,68,68,0.06)", opacity: 0.8 };
  if (state === "modified") return { borderLeft: "3px solid #f59e0b", paddingLeft: 6, background: "rgba(245,158,11,0.06)" };
  return {};
}


export default function TreeProposalCard({ proposal, existingTree, onAccept, onRefine, onDiscard, dismissed }) {
  const { currentUser } = useAuth();
  const canEdit = _DEV_ROLES.includes(currentUser?.role);
  const [refineText, setRefineText] = useState("");
  const [refineOpen, setRefineOpen] = useState(false);
  // §19.15 / G.2 — captured integration_id per placeholder. Indexed by
  // placeholder_id (or by index when the LLM didn't supply IDs). The
  // Configure modal pushes entries here; the card surfaces a green
  // "Configured" badge; on Accept, the captured set is appended to the
  // outgoing message so the LLM threads them through provision_agent_tree's
  // bindings.
  const [registered, setRegistered] = useState({}); // {placeholder_key: {id, name, archetype, for_expert_name}}
  const [configureTarget, setConfigureTarget] = useState(null); // {key, archetype, suggested_name, auth_hint, purpose, for_expert_name}

  if (!proposal || typeof proposal !== "object") return null;

  const sup = proposal.supervisor || {};
  const experts = Array.isArray(proposal.experts) ? proposal.experts : [];
  const integrations = proposal.integrations || {};
  const mcpBindings = integrations.mcp_server_bindings || [];
  const oapiBindings = integrations.openapi_integration_bindings || [];
  // §19.15 — placeholders are the SHAPE the LLM can emit when an expert
  // needs an external integration: {placeholder_id, archetype,
  // for_expert_name, suggested_name, auth_hint, purpose_summary}. The
  // user clicks Configure → pastes their actual spec → registered map.
  // Falls back to displaying generic bindings preview if the LLM emitted
  // the older binding shape (legacy compat).
  const placeholders = Array.isArray(proposal.integration_placeholders)
    ? proposal.integration_placeholders
    : [];
  const integrationCount = placeholders.length + mcpBindings.length + oapiBindings.length;
  const allConfigured = placeholders.length === 0 || placeholders.every((p, idx) => registered[p.placeholder_id || idx]);

  // §19.12 / G.4 — diff overlay: green/red/amber per-row when chat is
  // editing an existing supervisor (existingTree provided).
  const diff = useMemo(() => _computeTreeDiff(existingTree, experts), [existingTree, experts]);
  const isEditMode = !!existingTree;

  const isSupervisor = experts.length > 0 || sup.agent_kind === "supervisor";
  const supName = sup.name || (isSupervisor ? "Supervisor" : "Agent");
  const mode = (sup.flow_config?.supervisor?.mode) || (isSupervisor ? "fan_out" : null);
  const channels = Array.isArray(sup.channels) ? sup.channels : [];

  const cardOpacity = dismissed ? 0.45 : 1;
  const cardPointerEvents = dismissed ? "none" : "auto";

  return (
    <div style={{
      marginTop: 8, marginBottom: 4,
      border: `1px solid ${COLORS.border}`,
      borderRadius: 12,
      background: COLORS.bg,
      padding: 12,
      opacity: cardOpacity,
      pointerEvents: cardPointerEvents,
    }}>
      {/* Header — supervisor / agent identity */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 16 }}>{isSupervisor ? "🏗️" : "🤖"}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>
            {isSupervisor ? "Supervisor" : "Agent"}: {supName}
          </div>
          <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>
            {isSupervisor && mode && <span>mode: <code style={{ background: `${COLORS.accent}15`, padding: "1px 5px", borderRadius: 3 }}>{mode}</code> · </span>}
            channels: {channels.length > 0 ? channels.join(", ") : "chat"}
          </div>
        </div>
      </div>

      {/* Experts */}
      {(experts.length > 0 || (isEditMode && diff.removed.length > 0)) && (
        <div style={{ marginBottom: 8, paddingTop: 8, borderTop: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 4 }}>
            Experts ({experts.length})
            {isEditMode && diff.removed.length > 0 && (
              <span style={{ color: COLORS.pink || "#ef4444" }}>
                {" — " + diff.removed.length + " removed"}
              </span>
            )}
          </div>
          {experts.map((exp, idx) => {
            const archetype = exp.archetype || (exp.metadata_json?.archetype) || "specialized";
            const badge = _archBadge(archetype);
            const expertName = exp.name || `Expert ${idx + 1}`;
            const diffState = isEditMode ? (diff.byProposalIdx[idx] || "unchanged") : null;
            return (
              <div key={idx} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "5px 0",
                fontSize: 12,
                ..._diffStyle(diffState),
              }}>
                <span style={{ fontSize: 14 }}>
                  {archetype === "knowledge" ? "📚" : archetype === "openapi" ? "🔌" : archetype === "mcp" ? "🛠️" : archetype === "web" ? "🌐" : "💬"}
                </span>
                <span style={{ flex: 1, color: COLORS.text }}>{expertName}</span>
                {diffState === "added" && (
                  <span style={{ fontSize: 10, color: "#10b981", fontWeight: 600 }}>+ added</span>
                )}
                {diffState === "modified" && (
                  <span style={{ fontSize: 10, color: "#f59e0b", fontWeight: 600 }}>~ modified</span>
                )}
                <span style={{
                  fontSize: 10, padding: "2px 6px", borderRadius: 3,
                  background: `${badge.color}20`, color: badge.color,
                }}>{badge.label}</span>
                {!dismissed && canEdit && (
                  <ExpertEditMenu
                    expertName={expertName}
                    archetype={archetype}
                    onRefine={onRefine}
                  />
                )}
              </div>
            );
          })}
          {/* Removed experts (edit mode only) */}
          {isEditMode && diff.removed.map((rem, idx) => {
            const badge = _archBadge(rem.archetype);
            return (
              <div key={`rm-${idx}`} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "5px 0", fontSize: 12,
                ..._diffStyle("removed"),
              }}>
                <span style={{ fontSize: 14 }}>🗑️</span>
                <span style={{ flex: 1, color: COLORS.text, textDecoration: "line-through" }}>{rem.name}</span>
                <span style={{ fontSize: 10, color: "#ef4444", fontWeight: 600 }}>− removed</span>
                <span style={{
                  fontSize: 10, padding: "2px 6px", borderRadius: 3,
                  background: `${badge.color}20`, color: badge.color,
                }}>{badge.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Integration placeholders with [Configure →] (§19.15 / G.2) */}
      {integrationCount > 0 && (
        <div style={{ marginBottom: 8, paddingTop: 8, borderTop: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 4 }}>
            Integrations ({integrationCount})
          </div>
          {placeholders.map((ph, idx) => {
            const key = ph.placeholder_id || `ph-${idx}`;
            const reg = registered[key];
            const arch = (ph.archetype || "openapi").toLowerCase();
            const archIcon = arch === "mcp" ? "🛠️" : "🔌";
            return (
              <div key={key} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "5px 0", fontSize: 11,
              }}>
                <span style={{ fontSize: 14 }}>{archIcon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: COLORS.text, fontSize: 12 }}>
                    {ph.suggested_name || (arch === "mcp" ? "MCP server" : "OpenAPI integration")}
                    {ph.for_expert_name && (
                      <span style={{ color: COLORS.textDim }}> — for {ph.for_expert_name}</span>
                    )}
                  </div>
                  {ph.purpose_summary && (
                    <div style={{ fontSize: 10, color: COLORS.textDim }}>{ph.purpose_summary}</div>
                  )}
                </div>
                {reg ? (
                  <span style={{
                    fontSize: 10, padding: "2px 8px", borderRadius: 999,
                    background: `${COLORS.green}20`, color: COLORS.green,
                  }}>✓ Configured</span>
                ) : canEdit ? (
                  <button
                    type="button"
                    onClick={() => setConfigureTarget({
                      key, archetype: arch,
                      suggested_name: ph.suggested_name || "",
                      auth_hint: ph.auth_hint || "bearer",
                      purpose_summary: ph.purpose_summary || "",
                      for_expert_name: ph.for_expert_name || "",
                    })}
                    disabled={dismissed}
                    style={{
                      padding: "3px 9px", borderRadius: 6,
                      border: `1px solid ${COLORS.accent}55`,
                      background: "transparent",
                      color: COLORS.accentText,
                      fontSize: 10, fontWeight: 600,
                      cursor: dismissed ? "not-allowed" : "pointer",
                    }}
                  >Configure →</button>
                ) : (
                  <span style={{
                    fontSize: 10, color: COLORS.textMuted, fontStyle: "italic",
                  }}>not configured</span>
                )}
              </div>
            );
          })}
          {mcpBindings.map((b, idx) => (
            <div key={`mcp-${idx}`} style={{ fontSize: 11, color: COLORS.textMuted, padding: "2px 0" }}>
              📡 MCP server binding (id: {b.mcp_server_id || "—"})
            </div>
          ))}
          {oapiBindings.map((b, idx) => (
            <div key={`oapi-${idx}`} style={{ fontSize: 11, color: COLORS.textMuted, padding: "2px 0" }}>
              📡 OpenAPI binding (id: {b.openapi_integration_id || "—"})
            </div>
          ))}
        </div>
      )}

      <ConfigureIntegrationModal
        open={!!configureTarget}
        archetype={configureTarget?.archetype}
        suggestedName={configureTarget?.suggested_name}
        authHint={configureTarget?.auth_hint}
        purposeSummary={configureTarget?.purpose_summary}
        onClose={() => setConfigureTarget(null)}
        onRegistered={(integ) => {
          setRegistered(prev => ({
            ...prev,
            [configureTarget.key]: {
              id: integ.id,
              name: integ.name,
              archetype: integ.archetype,
              for_expert_name: configureTarget.for_expert_name,
            },
          }));
        }}
      />

      {/* Refine input (collapsible) — only authors can refine. */}
      {refineOpen && !dismissed && canEdit && (
        <div style={{ marginBottom: 8 }}>
          <textarea
            value={refineText}
            onChange={(e) => setRefineText(e.target.value)}
            placeholder="Tell me what to change. e.g. 'Add a returns expert' or 'Make expert #2 use the Slack MCP'."
            rows={2}
            style={{
              width: "100%",
              border: `1px solid ${COLORS.border}`,
              borderRadius: 6,
              background: COLORS.surface,
              color: COLORS.text,
              fontSize: 12, padding: 8,
              resize: "vertical",
              boxSizing: "border-box",
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                if (refineText.trim()) {
                  onRefine(refineText.trim());
                  setRefineText("");
                  setRefineOpen(false);
                }
              }
            }}
          />
          <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
            <button
              type="button"
              onClick={() => {
                if (!refineText.trim()) return;
                onRefine(refineText.trim());
                setRefineText("");
                setRefineOpen(false);
              }}
              disabled={!refineText.trim()}
              style={{
                padding: "5px 12px",
                borderRadius: 6,
                border: "none",
                background: refineText.trim() ? COLORS.accent : COLORS.surfaceHover,
                color: refineText.trim() ? "#fff" : COLORS.textDim,
                fontSize: 11, fontWeight: 600, cursor: refineText.trim() ? "pointer" : "not-allowed",
              }}
            >Send</button>
            <button
              type="button"
              onClick={() => { setRefineOpen(false); setRefineText(""); }}
              style={{
                padding: "5px 12px",
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                background: "transparent",
                color: COLORS.textMuted,
                fontSize: 11, cursor: "pointer",
              }}
            >Cancel</button>
          </div>
        </div>
      )}

      {/* Action buttons — authors only. Read-only viewers see a notice. */}
      {canEdit ? (
        <div style={{
          display: "flex", gap: 6,
          paddingTop: 8, borderTop: `1px solid ${COLORS.border}`,
        }}>
          <button
            type="button"
            onClick={() => {
              // §19.15 / G.2 — when integrations are configured, attach
              // them as a structured block to the outgoing message so
              // the LLM threads the captured ids into provision_agent_tree's
              // bindings list. Empty placeholders OR fully-configured
              // proposal → no block; legacy (no placeholders) → no block.
              const captured = Object.entries(registered).map(([k, v]) => ({
                placeholder_id: k,
                integration_id: v.id,
                integration_name: v.name,
                archetype: v.archetype,
                for_expert_name: v.for_expert_name,
              }));
              onAccept && onAccept(proposal, captured);
            }}
            disabled={dismissed || !allConfigured}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: "none",
              background: (dismissed || !allConfigured) ? COLORS.surfaceHover : COLORS.accent,
              color: (dismissed || !allConfigured) ? COLORS.textDim : "#fff",
              fontSize: 12, fontWeight: 600,
              cursor: (dismissed || !allConfigured) ? "not-allowed" : "pointer",
            }}
            title={!allConfigured ? "Configure all integrations before provisioning" : "All integrations configured — click to provision"}
          >{dismissed ? "Discarded" : "Accept"}</button>
          {/* "Create now, wire later" — provision the agent + experts
              without integration bindings. The user binds integrations
              from the Wiring tab when ready. The Super Agent's prompt
              has a "Path B" branch that handles an empty
              integration_registrations block by calling
              provision_agent_tree with empty bindings.
              Hidden when there are no placeholders (no integrations to
              defer in the first place) or when everything's already
              configured (Accept already does the right thing). */}
          {!dismissed && placeholders.length > 0 && !allConfigured && (
            <button
              type="button"
              onClick={() => {
                // Empty captured array tells the LLM: provision now
                // with no bindings; user will wire later.
                onAccept && onAccept(proposal, []);
              }}
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                border: `1px solid ${COLORS.border}`,
                background: "transparent",
                color: COLORS.text,
                fontSize: 12, fontWeight: 500,
                cursor: "pointer",
              }}
              title="Provision the agent now without integration bindings; you can wire them later from the Wiring tab"
            >Create now, wire later</button>
          )}
          <button
            type="button"
            onClick={() => setRefineOpen((v) => !v)}
            disabled={dismissed}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: `1px solid ${COLORS.border}`,
              background: "transparent",
              color: COLORS.text,
              fontSize: 12,
              cursor: dismissed ? "not-allowed" : "pointer",
            }}
          >Refine ↗</button>
          <button
            type="button"
            onClick={() => onDiscard && onDiscard()}
            disabled={dismissed}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              border: `1px solid ${COLORS.border}`,
              background: "transparent",
              color: COLORS.textMuted,
              fontSize: 12,
              cursor: dismissed ? "not-allowed" : "pointer",
              marginLeft: "auto",
            }}
          >Discard</button>
        </div>
      ) : (
        <div style={{
          paddingTop: 8, borderTop: `1px solid ${COLORS.border}`,
          fontSize: 11, color: COLORS.textMuted, fontStyle: "italic",
        }}>
          Read-only — only developers can accept or refine proposals.
        </div>
      )}
    </div>
  );
}


// §19.12 / G.3 — inline per-expert edit menu. Click ⋯ → small popover
// with Rename / Change archetype / Remove. Each option emits a typed
// refinement instruction back to the LLM via onRefine; the LLM emits
// a new tree_proposal block reflecting the change. Avoids the user
// having to type free-form refinements in the textarea for the common
// edit operations.
function ExpertEditMenu({ expertName, archetype, onRefine }) {
  const [open, setOpen] = useState(false);
  const [archeOpen, setArcheOpen] = useState(false);

  const renameAction = () => {
    setOpen(false);
    const next = window.prompt(`Rename "${expertName}" to:`, expertName);
    if (!next || !next.trim() || next.trim() === expertName) return;
    onRefine && onRefine(`Rename expert "${expertName}" to "${next.trim()}".`);
  };

  const removeAction = () => {
    setOpen(false);
    const ok = window.confirm(`Remove "${expertName}" from the proposal?`);
    if (!ok) return;
    onRefine && onRefine(`Remove the "${expertName}" expert from the proposal.`);
  };

  const setArchetype = (newArche) => {
    setArcheOpen(false);
    setOpen(false);
    if (newArche === archetype) return;
    onRefine && onRefine(`Change the "${expertName}" expert's archetype to ${newArche}.`);
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "2px 7px", borderRadius: 4,
          border: "none",
          background: "transparent",
          color: COLORS.textMuted,
          fontSize: 13, cursor: "pointer", lineHeight: 1,
        }}
        title="Edit this expert"
      >⋯</button>
      {open && (
        <>
          {/* Click-outside scrim — replaces the previous `onMouseLeave`
              dismissal which fired the moment a user's mouse path between
              "Change archetype" and a submenu item briefly exited the
              container bounds, collapsing the menu before the submenu
              click could register. */}
          <div
            onClick={() => { setOpen(false); setArcheOpen(false); }}
            style={{ position: "fixed", inset: 0, zIndex: 49 }}
          />
        <div
          style={{
            position: "absolute",
            right: 0, top: "100%", marginTop: 2,
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 6,
            padding: 4,
            zIndex: 50,
            minWidth: 170,
            boxShadow: "0 4px 12px rgba(0,0,0,0.35)",
          }}
        >
          <MenuItem onClick={renameAction}>Rename…</MenuItem>
          <MenuItem onClick={() => setArcheOpen((v) => !v)}>
            Change archetype ▸
          </MenuItem>
          {archeOpen && (
            <div style={{ paddingLeft: 10 }}>
              {["knowledge", "openapi", "mcp", "web", "specialized"].map((a) => (
                <MenuItem
                  key={a}
                  onClick={() => setArchetype(a)}
                  highlight={a === archetype}
                >{a}{a === archetype ? " (current)" : ""}</MenuItem>
              ))}
            </div>
          )}
          <div style={{ height: 1, background: COLORS.border, margin: "4px 0" }} />
          <MenuItem onClick={removeAction} danger>Remove…</MenuItem>
        </div>
        </>
      )}
    </div>
  );
}


function MenuItem({ children, onClick, danger, highlight }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "block", width: "100%",
        padding: "5px 9px",
        border: "none",
        background: "transparent",
        color: danger ? COLORS.pink : (highlight ? COLORS.accentText : COLORS.text),
        fontSize: 11, textAlign: "left",
        cursor: "pointer",
        borderRadius: 3,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.surfaceHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >{children}</button>
  );
}


// Helper for AgentBuilderChat — extracts the tree_proposal JSON from a
// markdown reply. Returns {proposal, contentWithoutBlock} or
// {proposal: null, contentWithoutBlock: original} when no block found.
//
// Looks for ```tree_proposal\n{...}\n``` (loose match — allows
// optional leading/trailing whitespace). The extracted block is
// stripped from the displayed content so the user sees the prose
// confirmation message but the JSON renders as the structured card.
export function extractTreeProposal(content) {
  if (typeof content !== "string") return { proposal: null, contentWithoutBlock: content };
  const fenceRx = /```tree_proposal\s*\n([\s\S]*?)\n```/m;
  const match = fenceRx.exec(content);
  if (!match) return { proposal: null, contentWithoutBlock: content };
  let proposal = null;
  try {
    proposal = JSON.parse(match[1].trim());
  } catch {
    // Malformed JSON — leave the block in the content so the user can
    // see what the LLM emitted, no card rendered.
    return { proposal: null, contentWithoutBlock: content };
  }
  const before = content.slice(0, match.index).trimEnd();
  const after = content.slice(match.index + match[0].length).trimStart();
  const contentWithoutBlock = [before, after].filter(Boolean).join("\n\n");
  return { proposal, contentWithoutBlock };
}
